import { useState } from "react";

import { TStep } from "../TeamsIntegrationModal.types";

import { AlertColor, MAX_FILE_UPLOAD, MAX_FILES_SIZE } from "@/util/constants";
import { TeamsFile } from "@/util/interfaces/TeamsFile";

type TProps = {
  callbackClose: (_message?: string, _color?: AlertColor) => void;
  uploadTeamsFiles: () => Promise<boolean>;
};

type TUseGetTeamsIntegrationModalStepsReturn = {
  getSubmitButtonText: (_step: TStep) => string;
  getCancelButtonText: (_step: TStep) => string;
  handleSubmit: () => void;
  handleCancel: () => void;
  getSubmitButtonDisabled: (_files: TeamsFile[], disable: boolean) => [boolean, string?];
  step: TStep;
  loadingButton: boolean;
};

export function useGetTeamsIntegrationModalSteps({
  callbackClose,
  uploadTeamsFiles,
}: TProps): TUseGetTeamsIntegrationModalStepsReturn {
  const [step, setStep] = useState<TStep>("fileList");
  const [loadingButton, setLoadingButton] = useState(false);

  const getSubmitButtonText = (step: TStep) => {
    const texts = {
      fileList: "Next",
      metadata: "Next",
      review: "Upload",
      warning: "Import",
    };

    return texts[step];
  };

  const getCancelButtonText = (step: TStep) => {
    const texts = {
      fileList: "Cancel",
      metadata: "Previous",
      review: "Previous",
      warning: "Cancel",
    };

    return texts[step];
  };

  const handleSubmit = async () => {
    if (step === "fileList") {
      setStep("metadata");
      return;
    }

    if (step === "metadata") {
      setStep("review");
      return;
    }

    if (step === "review") {
      setLoadingButton(true);
      try {
        await uploadTeamsFiles();
      } finally {
        setLoadingButton(false);
      }
      return;
    }

    if (step === "warning") {
      callbackClose();
    }
  };

  const handleCancel = () => {
    if (step === "fileList") {
      callbackClose();
      return;
    }

    if (step === "metadata") {
      setStep("fileList");
      return;
    }

    if (step === "review") {
      setStep("metadata");
      return;
    }

    if (step === "warning") {
      setStep("review");
    }
  };

  const getSubmitButtonDisabled = (files: TeamsFile[], disable = false): [boolean, string?] => {
    if (step === "fileList") {
      if (disable) {
        return [true, undefined];
      }
      if (files.length === 0) {
        return [true, undefined];
      }
      if (files.length > MAX_FILE_UPLOAD) {
        return [true, `The maximum number of files you can upload is ${MAX_FILE_UPLOAD}`];
      }
      let totalFileSize = files.reduce((acum, f) => acum + f.size, 0);
      totalFileSize = totalFileSize / 1024 / 1024;
      if (totalFileSize > MAX_FILES_SIZE) {
        return [true, `Total files size exceed the maximum that is ${MAX_FILES_SIZE}MB.`];
      }
    }
    if (step === "metadata") {
      return [files.some((f) => !f.category), undefined];
    }
    return [false, undefined];
  };

  return {
    getSubmitButtonText,
    getCancelButtonText,
    handleSubmit: () => {
      void handleSubmit();
    },
    handleCancel,
    getSubmitButtonDisabled,
    step,
    loadingButton,
  };
}
